import React from 'react';
import { TranslateFunction } from 'i18n/useTranslationModules';
import classNames from 'classnames';

import {
	BLChannelProfile,
	RegistrationQuestion,
	IRegistrationQuestionTranslations,
	TranslateString,
	LanguagesAbbr,
	RegFieldsEnum,
} from '../../../types/working-model';
import Icon, { ICONS } from '../../general-ui/icon';
import { useTranslate } from '../../../i18n/useTranslationModules';
import { useTypedSelector } from '../../../store/reducers/use-typed-selector';
import { showField } from './profile-utils.ts';


function getFieldName(question: RegistrationQuestion, t: TranslateFunction, language: LanguagesAbbr, registrationSettingsTranslations?: IRegistrationQuestionTranslations) {
	// first check registration_settings.registration_questions_translations[question.registration_question].name[language]
	const translatedName = registrationSettingsTranslations?.[question.registration_question]?.name?.[language];
	if (translatedName && typeof translatedName === 'string') {
		return translatedName;
	}

	// then go back to fallback
	return question.global
		? t(`registration.registration_fields.${question.registration_question}` as string)
		: (question.name_translation?.[language] as string ?? question.name);
}

interface ProfileFieldsProps {
	questions: RegistrationQuestion[] | undefined;
	profile: BLChannelProfile;
	t: TranslateFunction;
	language?: LanguagesAbbr;
	showPersonalProfile?: boolean;
}

const alwaysBannedFields = [
	RegFieldsEnum.passcode,
	RegFieldsEnum.attend_in_person
];

export const ProfileFields: React.FC<ProfileFieldsProps> = ({
	questions,
	profile,
	t,
	language,
	showPersonalProfile,
}) => {
	const liveTranslations = useTypedSelector(state => state.LiveEventReducer?.eventBundle?.registration_settings?.registration_questions_translations);
	const editorTranslations = useTypedSelector(state => state.CreateEventReducer?.workingEvent?.registration_settings?.registration_questions_translations);
	const registrationSettingsTranslations = liveTranslations || editorTranslations;
	const currentUserBlProfile = useTypedSelector(state => state.LiveEventReducer.blProfileUser?.bl_profile);
	const fields: JSX.Element[] = [];
	if (!language) return <></>;

	questions?.forEach(question => {
		const id = String(question.registration_question);

		const isEmailField = question.registration_question === RegFieldsEnum.email || question.type === 'email';
		const isOwnProfile = currentUserBlProfile && profile?.bl_profile && currentUserBlProfile === profile?.bl_profile;
		const shouldShowEmailField = !isEmailField || (isEmailField && isOwnProfile);
		// unless it is personal editable profile don't show empty fields or sections
		// We don't need to show if they're attending in person.
		// And always hide the passcode field
		if (
			shouldShowEmailField
			&& (showPersonalProfile || showField(question, profile))
			&& !alwaysBannedFields.includes(question.registration_question)
		) {
			const name = getFieldName(question, t, language);
			fields.push(<CardItem registrationQuestionId={question.registration_question} key={id} title={name} content={profile?.[id]} t={t} multiLine={question.type === 'textarea'} language={language} />);
		}
	});

	return <>{fields}</>;
};

interface CardItemProps {
	title: string;
	content: string | string[];
	t: TranslateFunction;
	multiLine?: boolean;
	language?: string;
	registrationQuestionId: number;
}
const CardItem = ({ title, content, t, multiLine = false, registrationQuestionId, language }: CardItemProps) => {
	const liveTranslations = useTypedSelector(state => state.LiveEventReducer?.eventBundle?.registration_settings?.registration_questions_translations);
	const editorTranslations = useTypedSelector(state => state.CreateEventReducer?.workingEvent?.registration_settings?.registration_questions_translations);
	const registrationSettingsTranslations = liveTranslations || editorTranslations;

	const renderContent = () => {
		if (typeof content === 'boolean') {
			return <p className={classNames({ 'multi-line': multiLine })}>
				{
					content
						? t('registration.Yes')
						: t('registration.No')
				}
			</p>;
		} else if (Array.isArray(content)) {
			const options = registrationSettingsTranslations?.[registrationQuestionId]?.options;
			const optionMapByBaseName = options && Object.values(options).reduce((_options, option) => {
				_options[option.base] = option;
				return _options;
			}, {} as { [key: string]: TranslateString });
			return (
				<ul className={classNames('card-topic-tags', { 'multi-line': multiLine })}>
					{content.map(tag => {
						let _tag = tag;
						const dbTranslation = language && optionMapByBaseName?.[tag]?.[language];
						if (dbTranslation && typeof dbTranslation === 'string') {
							_tag = dbTranslation;
						}
						return <li key={_tag}>{_tag}</li>;
					})}
				</ul>
			);
		} else {
			// if registrationQuestionId has options, find it
			const options = registrationSettingsTranslations?.[registrationQuestionId]?.options;
			let _content = content;
			if (options) {
				const matchedOption = Object.values(options).find(option => option.base === content);
				const optionTranslation = matchedOption && language && matchedOption[language];
				if (optionTranslation && typeof optionTranslation === 'string') {
					_content = optionTranslation;
				}
			}
			return <p className={classNames({ 'multi-line': multiLine })}>{_content}</p>;
		}
	};

	return (
		<div className="profile-card-item">
			<div className="card-item-content">
				<h5>{title}</h5>
				{renderContent()}
			</div>
		</div >
	);
};

interface CardTitleProps {
	title: string;
	handleClick: () => void;
	disabled: boolean;
	canEdit?: boolean;
	className?: string;
}
export const CardTitle = ({ title, handleClick, disabled, canEdit = true, className = '' }: CardTitleProps): JSX.Element => {
	const { t } = useTranslate('homepage');

	return (
		<div className={'card-title ' + className}>
			<h3>{title}</h3>
			{canEdit ? (
				<button className="edit-button no-style" onClick={handleClick} disabled={disabled}>
					<Icon name={ICONS.EDIT} size={12} color="" />
					{t('homepage:Edit', t('Edit', ''))}
				</button>
			) : null}
		</div>
	);
};

interface LeaderboardPointTotalProps {
	headerText: string;
	points: number;
}
export const LeaderboardPointTotal = ({ headerText, points }: LeaderboardPointTotalProps): JSX.Element => (
	<div className="leaderboard-points-total">
		<h5>{headerText}</h5>
		<h2 className="leaderboard-points-total-amount">{points}</h2>
	</div>
);

interface LeaderboardActionProps {
	actionName: string;
	pointsEarned: number;
	pointMultiplier: number | null;
}
export const LeaderboardAction = ({ actionName, pointsEarned, pointMultiplier }: LeaderboardActionProps): JSX.Element => {
	const { t } = useTranslate('homepage');

	return (
		<div className="leaderboard-action">
			<div className="left">
				<div className="leaderboard-action-name">{actionName}</div>
				{pointMultiplier && <h5>{pointMultiplier} {pointMultiplier > 1 ? t('leaderboard.Points Per Action') : t('leaderboard.Point Per Action')}</h5>}
			</div>
			<div>
				<h5>{t('leaderboard.Points Earned')}</h5>
				<div className="leaderboard-action-points-earned-amount">{pointsEarned}</div>
			</div>
		</div>
	);
};
